import { getOfflineDb } from './connection';
import { isGateEnabled, isReadAllowed } from './meta';

import type { OfflineDbExecutor } from './driver-types';

/**
 * The seam between TanStack query functions and the encrypted offline cache.
 *
 * Every hydration path acquires its handle here rather than calling
 * `getOfflineDb` directly, so the two conditions that make caching legitimate
 * are checked in ONE place instead of once per feature: the org's kill-switch
 * gate, and the R15 offline-read bound.
 *
 * Nothing here throws. The cache is disposable and strictly an optimisation -
 * a query must never fail because the cache could not be opened, and a caller
 * that gets `null` simply behaves as though offline storage did not exist.
 * Off native (`getOfflineDb` returns null) that is exactly what happens, which
 * is what keeps the web build's behaviour bit-for-bit unchanged.
 */
async function openCache(
  ownerUserId: string | undefined,
  organizationId: string | undefined,
): Promise<OfflineDbExecutor | null> {
  if (!ownerUserId || !organizationId) {
    return null;
  }
  try {
    const cache = await getOfflineDb(ownerUserId);
    if (!cache) {
      return null;
    }
    // The gate is an emergency kill-switch: flipped off, the cache stops
    // being read AND stops being written, immediately. It is evaluated PER
    // ORGANISATION on the server, so it is answered per organisation here -
    // one device-wide flag let an org that is still enabled re-open the cache
    // for an org whose switch had been flipped off.
    return (await isGateEnabled(cache, organizationId)) ? cache : null;
  } catch {
    return null;
  }
}

/**
 * A handle for WRITING cached rows, valid whenever the gate is on.
 *
 * Deliberately not bound by R15: the lock governs how long a device may keep
 * READING without re-verifying, and write-through only ever persists rows the
 * server just authorised. Refusing writes while locked would also mean a
 * device that re-verifies has nothing fresh to show for it.
 */
export async function getOfflineWriteCache(
  ownerUserId: string | undefined,
  organizationId: string | undefined,
): Promise<OfflineDbExecutor | null> {
  return await openCache(ownerUserId, organizationId);
}

/**
 * A handle for READING cached rows, valid only inside the R15 window.
 *
 * This is the FIRST of two layers. The executor itself refuses payload reads
 * when the stamp is stale (U8b), but that layer answers with an empty result -
 * indistinguishable from an empty cache. Checking here lets a locked cache
 * report itself as absent, so the caller falls through to the network and the
 * user sees an honest error state rather than a convincingly empty inbox.
 */
export async function getOfflineReadCache(
  ownerUserId: string | undefined,
  organizationId: string | undefined,
  nowMs: number = Date.now(),
): Promise<OfflineDbExecutor | null> {
  const cache = await openCache(ownerUserId, organizationId);
  if (!cache) {
    return null;
  }
  try {
    return (await isReadAllowed(cache, nowMs)) ? cache : null;
  } catch {
    return null;
  }
}

/**
 * Parse a cached JSON payload, treating malformed content as a cache miss.
 *
 * Payload columns are opaque strings by design, so nothing guarantees a row
 * still parses - a truncated write or a shape from an older app version would
 * otherwise throw inside a queryFn and fail the whole list. One unreadable row
 * costs one row.
 */
export function parseCachedPayload<T>(payload: string): T | null {
  try {
    return JSON.parse(payload) as T;
  } catch {
    return null;
  }
}
